import type {
  ClientDTO,
  CreateClientRequest,
  InvitePortalRequest,
  InvitePortalResponse,
  ListClientsQuery,
  Paginated,
  UpdateClientRequest,
} from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { toast } from '../../components/ui/Toast';
import { clientsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function useClientsList(query: ListClientsQuery): UseQueryResult<Paginated<ClientDTO>> {
  return useQuery({
    queryKey: queryKeys.clients.list(query as Record<string, unknown>),
    queryFn: ({ signal }) => clientsApi.list(query, signal),
    placeholderData: (prev) => prev, // keep previous page visible while fetching
  });
}

export function useClient(id: string): UseQueryResult<ClientDTO> {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: ({ signal }) => clientsApi.get(id, signal),
    enabled: id !== '',
  });
}

export function useClientTimeline(id: string) {
  return useQuery({
    queryKey: queryKeys.clients.timeline(id),
    queryFn: ({ signal }) => clientsApi.timeline(id, signal),
    enabled: id !== '',
    // Poll while anything on this client's timeline is still "in flight" —
    // a questionnaire response not yet RESPONDED, or a report still
    // PENDING/GENERATING (CP-P2-T03: reports can now be auto-triggered by
    // the client's own submission, not just a coach's Report Studio
    // action) — so a coach on this page sees both without a manual
    // refresh. Mirrors useClientQuestionnaires' refetchInterval condition.
    refetchInterval: (query) => {
      const t = query.state.data;
      if (!t) return false;
      const hasPending = t.questionnaires.some((q) => q.dispatchStatus !== 'RESPONDED');
      const hasInProgressReport = t.reports.some(
        (r) => r.status === 'PENDING' || r.status === 'GENERATING',
      );
      return hasPending || hasInProgressReport ? 5000 : false;
    },
  });
}

export function useCreateClient(): UseMutationResult<ClientDTO, unknown, CreateClientRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateClientRequest) => clientsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast.success('Client created');
    },
  });
}

export function useUpdateClient(
  id: string,
): UseMutationResult<ClientDTO, unknown, UpdateClientRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateClientRequest) => clientsApi.update(id, body),
    onSuccess: (client) => {
      qc.setQueryData(queryKeys.clients.detail(id), client);
      void qc.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast.success('Client updated');
    },
  });
}

export function useDeleteClient(): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsApi.remove(id),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: queryKeys.clients.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast.success('Client deleted');
    },
  });
}

export function useInvitePortal(
  clientId: string,
): UseMutationResult<InvitePortalResponse, unknown, InvitePortalRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvitePortalRequest) => clientsApi.invitePortal(clientId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clients.timeline(clientId) });
    },
  });
}
